import React, { useState, useEffect } from 'react';
import { MapPin, User } from 'lucide-react';
import { FaLinkedin, FaXTwitter, FaGithub } from 'react-icons/fa6';
import { getExpertAvatarUrl, EXPERT_AVATAR_FALLBACK } from '../utils/imageUtils';
import { ExpertRating } from './ExpertRating';
import { useChatbot } from '../contexts/ChatbotContext';
import { anonymizeLastName } from '../utils/anonymizeNames';

export interface ExpertCardProps {
  _id?: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  profileImage?: string;
  rating?: number;
  city?: string;
  linkedin?: string;
  social?: {
    twitter?: string;
    github?: string;
  };
  email?: string;
  phone?: string;
  clientType?: string;
  isPlaceholder?: boolean;
}

export const ExpertCard: React.FC<ExpertCardProps> = ({
  _id: _expertId,
  firstName,
  lastName,
  role,
  profileImage,
  social = {},
  rating,
  city,
  linkedin,
  email: _email,
  phone: _phone,
  clientType: _clientType,
  isPlaceholder = false,
}) => {
  void _expertId;
  const { showMessage } = useChatbot();
  const [, setImageError] = useState(false);
  const [imageUrl, setImageUrl] = useState<string>(() => getExpertAvatarUrl(profileImage ?? ''));

  // Masquage du nom de famille côté affichage (confidentialité).
  const maskedLastName = anonymizeLastName(lastName ?? '');
  const maskedFullName = `${firstName ?? ''} ${maskedLastName}`.trim();

  useEffect(() => {
    setImageUrl(getExpertAvatarUrl(profileImage ?? ''));
    setImageError(false);
  }, [profileImage]);

  const handleImageError = () => {
    setImageError(true);
    setImageUrl(`${EXPERT_AVATAR_FALLBACK}?v=${Date.now()}`);
  };

  const handleImageLoad = () => {
  };

  const handleLinkedInClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!linkedin) {
      showMessage('Le profil LinkedIn n\'est pas disponible', 'error');
      return;
    }
    window.open(linkedin, '_blank', 'noopener,noreferrer');
  };

  if (isPlaceholder) {
    return (
      <div className="bg-[#2F3136] rounded-lg overflow-hidden">
        <div className="aspect-square bg-[#5865F2]/10 flex items-center justify-center">
          <User size={64} className="text-[#5865F2]/40" />
        </div>
        <div className="p-4">
          <span className="inline-block bg-[#5865F2]/20 text-[#5865F2] text-xs font-semibold px-3 py-1 rounded-full mb-2">
            Rejoint bientôt
          </span>
          <p className="text-gray-500 text-sm">Profil en cours de création</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className="bg-[#2F3136] rounded-lg overflow-hidden hover:translate-y-[-5px] transition-transform duration-300"
        role="presentation"
      >
        <div className="aspect-square bg-gray-800 relative">
          <img
            key={imageUrl}
            src={imageUrl}
            alt={maskedFullName}
            loading="lazy"
            className="w-full h-full object-cover"
            onError={handleImageError}
            onLoad={handleImageLoad}
          />
          {linkedin && (
            <button 
              onClick={handleLinkedInClick}
              className="absolute bottom-4 left-4 bg-[#0077B5]/50 hover:bg-[#0077B5] p-1.5 rounded-full transition-all duration-300 z-10"
              style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
            >
              <FaLinkedin size={18} className="text-white/80 hover:text-white transition-colors duration-300" />
            </button>
          )}
          {/* Badge note ou Nouveau */}
          {rating && rating > 0 ? (
            <ExpertRating rating={rating} className="absolute bottom-4 right-4" />
          ) : (
            <span className="absolute bottom-4 right-4 bg-[#5865F2] text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg">Nouveau</span>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#36393F] to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300 flex items-end">
            <div className="p-4 w-full">
              <div className="flex justify-center space-x-3">
                {social.twitter && (
                  <a href={social.twitter} target="_blank" rel="noopener noreferrer" className="text-white hover:text-[#5865F2] transition-colors">
                    <FaXTwitter size={20} />
                  </a>
                )}
                {social.github && (
                  <a href={social.github} target="_blank" rel="noopener noreferrer" className="text-white hover:text-[#5865F2] transition-colors">
                    <FaGithub size={20} />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="p-4">
          <h3 className="text-lg font-semibold text-white">{maskedFullName}</h3>
          <p className="text-[#5865F2] mb-2">
            {role === 'fondateur' ? 'Fondateur' : (role === 'freelancer' || role === 'freelancer_admin' ? 'Freelancer' : 'Expert')}
          </p>
          {city && (
            <div className="flex items-center mt-2 text-gray-400">
              <MapPin size={16} className="mr-1" />
              <span>{city}</span>
            </div>
          )}
        </div>
      </div>
    </>
  );
}; 